import React, { FC, useCallback, useContext, useMemo, useState, useRef, useEffect } from 'react';
import { useEnumState } from '../../hooks/useEnumState';
import { OrganizationProductsFetcher } from './CustomerFetchers';
import { UrlQueryDataGrid } from '../UrlQueryDataGrid/UrlQueryDataGrid';
import { GridColumns, GridRowData, GridRowParams, GridSelectionModel, GridApi } from '@mui/x-data-grid';
import { LicenseExpiry } from '../LicenseExpiry/LicenseExpiry';
import Button from '@mui/material/Button';
import { CustomerNewProduct } from './CustomerNewProduct';
import { getStringEqualsOperator } from '../../filterOperators/stringEqualsOperator';
import { getGreaterThanOperator } from '../../filterOperators/greaterThanOperator';
import { getLessThanOperator } from '../../filterOperators/lessThanOperator';
import { getStringDiffersOperator } from '../../filterOperators/stringDiffersOperator';
import { getEnumIsFilterOperator, getEnumIsNotFilterOperator } from '../../filterOperators/enumFilterOperator';
import { getDateAfterFilterOperator, getDateBeforeFilterOperator } from '../../filterOperators/dateFilterOperators';
import { useAutoFetch } from '../../contexts/Fetcher';
import { useTranslation } from 'react-i18next';
import { useQueryParam } from '../../hooks/useQueryParam';
import { usePageSize } from '../../hooks/usePageSize';
import { createStatusChipComponent } from '../StatusChip/StatusChip';
import { getLicenseStatus } from '../../helpers/licenseStatus';
import { AuthenticatedRoutes } from '../../routers/AuthenticatedRoutes';
import { useDialog } from '../../hooks/useDialog';
import { apiOrganizationProduct } from '../../resources/api/apiOrganizationProduct';
import { Link } from 'react-router-dom';
import { useIsGuardedRouteAllowed } from '../../resources/GuardedRouteLink';
import { CustomerProductPlanType } from '../../models/CustomerProductPlanType';
import Tooltip from '@mui/material/Tooltip';
import { UUID } from '../../types/uuid';
import styled from '@mui/styles/styled';
import { LazyUser } from '../LazyData/LazyUser';
import { EOrganizationSelectionStatus, OrganizationContext } from '../../contexts/OrganizationContext';
import { DateFormat } from '../../helpers/dateFormat';
import { getDateValueFormatter } from '../../valueFormatters/DateValueFormatter';
import { CustomerProductBatchRenew } from './CustomerProductBatchRenew';
import { CustomerProductBulkEdit } from './CustomerProductBulkEdit';
import { LicenseAvailabilityCell, ILicenseMetadataItem } from './LicenseAvailabilityCell';
import { useApi } from '../../hooks/useApi';
import { organizationProductResources } from '../../resources/builders/organizationProductResources';
import { resolveResourceUrl } from '../../resources/resource';

interface IOrganizationLicensesMetadataResponseItem {
  productId: number;
  licenses: ILicenseMetadataItem[];
  /** Present when this product failed (e.g. not in organization); licenses may be empty. */
  error?: string;
}

const ButtonWrapper = styled('div')(({ theme }) => ({
  padding: `${theme.spacing(2)} 0`,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const StatusChip = createStatusChipComponent({
  valid: ['common.licenseStatus.valid', 'lime'],
  expired: ['common.licenseStatus.expired', 'default'],
  expiresSoon: ['common.licenseStatus.expiresSoon', 'orange'],
  suspended: ['common.licenseStatus.suspended', 'red'],
});
const ErrorChip = createStatusChipComponent({
  error: ['common.licenseMode.error', 'red'],
});

const { useCreateOrganizationProduct } = apiOrganizationProduct;

const NotAssigned = styled('span')(({ theme }) => ({
  color: theme.palette.text.disabled,
}));

const AssigneesContainer = styled('div')({
  '& span:not(:last-of-type):after': {
    content: '", "',
  },
});

const DealIdLink = styled(Link)(({ theme }) => ({
  color: theme.palette.info.main,
  textDecoration: 'underline',
}));

const BulkRenewButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(1),
}));

interface IProps {
  hideDiscovery?: boolean;
}

export const OrganizationProducts: FC<IProps> = ({ hideDiscovery }) => {
  const { data, isLoading } = useContext(OrganizationProductsFetcher.Context);

  const { organization, selectionStatus: organizationContextType } = useContext(OrganizationContext);

  const { Guard: GuardNewProduct } = useCreateOrganizationProduct(organization ?? void 0);

  useAutoFetch(OrganizationProductsFetcher);

  const { t } = useTranslation();

  const { isOpen: isNewProductDialogOpen, openUrl: newUrl, close: abortCreate } = useDialog('new');

  // Get pagination info from URL params
  const pageParam = useQueryParam('page');
  const pageSize = usePageSize();
  const currentPage = pageParam ? parseInt(pageParam, 10) - 1 : 0;

  // State for managing selected rows
  const [selectedRows, setSelectedRows] = useState<GridSelectionModel>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);

  // State for batch renew dialog
  const [isBatchRenewDialogOpen, setIsBatchRenewDialogOpen] = useState(false);
  // State for bulk edit dialog
  const [isBulkEditDialogOpen, closeBulkEditDialog, handleBulkEdit] = useEnumState(false, false, true);

  // State for storing license data by product ID
  const [licenseData, setLicenseData] = useState<Map<number, ILicenseMetadataItem[]>>(new Map());
  const [loadingProductIds, setLoadingProductIds] = useState<Set<number>>(new Set());
  const [productIdsToLoadLicenses, setProductIdsToLoadLicenses] = useState<Set<number>>(new Set());

  // Ref to access grid API
  const gridApiRef = useRef<GridApi | null>(null);
  // Ref to track which product IDs are currently being fetched to prevent duplicate calls
  const fetchingProductIdsRef = useRef<Set<number>>(new Set());
  // Ref to track which product IDs have already been fetched
  const fetchedProductIdsRef = useRef<Set<number>>(new Set());

  const api = useApi({ indicateLoading: false });

  const customerProducts = useMemo(
    () =>
      hideDiscovery
        ? data?.customerProducts.filter(({ productName }) => productName.toLowerCase() !== 'discovery')
        : data?.customerProducts,
    [data, hideDiscovery]
  );

  const handleSelectionChange = useCallback((newSelection: GridSelectionModel) => {
    setSelectedRows(newSelection);

    // Convert row IDs to product IDs - row IDs are the same as product IDs
    const productIds = newSelection.map((rowId) => String(rowId));
    setSelectedProductIds(productIds);
  }, []);

  const handleBulkRenew = useCallback(() => {
    setIsBatchRenewDialogOpen(true);
  }, []);

  const closeBatchRenewDialog = useCallback(() => {
    setIsBatchRenewDialogOpen(false);
  }, []);

  // Extracts product IDs for the current page from the grid API
  const getVisibleProductIds = useCallback((): number[] => {
    if (!gridApiRef.current) return [];

    const gridApi = gridApiRef.current;
    const startIndex = currentPage * pageSize;
    const endIndex = startIndex + pageSize;

    const allRowModels = gridApi.getVisibleRowModels();
    const visibleProductIds: number[] = [];

    allRowModels.forEach((row: GridRowData, rowId: string | number) => {
      const rowIndex = gridApi.getRowIndex(rowId);
      if (rowIndex >= startIndex && rowIndex < endIndex) {
        visibleProductIds.push(row.id as number);
      }
    });

    return visibleProductIds;
  }, [currentPage, pageSize]);

  // When products are loaded or page/pageSize changes, fetch licenses for visible rows
  useEffect(() => {
    if (!customerProducts?.length) return;

    const productIds = getVisibleProductIds();
    if (productIds.length === 0) return;

    setProductIdsToLoadLicenses(new Set(productIds));
  }, [customerProducts, currentPage, pageSize, getVisibleProductIds]);

  // Fetch licenses when productIdsToLoadLicenses changes
  useEffect(() => {
    if (!organization || productIdsToLoadLicenses.size === 0) {
      return;
    }

    const productIdsToFetch = Array.from(productIdsToLoadLicenses).filter(
      (id) => !fetchedProductIdsRef.current.has(id) && !fetchingProductIdsRef.current.has(id)
    );
    if (productIdsToFetch.length === 0) return;

    productIdsToFetch.forEach((id) => fetchingProductIdsRef.current.add(id));
    setLoadingProductIds((prev) => new Set([...prev, ...productIdsToFetch]));

    (async () => {
      try {
        const resource = organizationProductResources.getOrganizationLicensesMetadata(organization);
        const resourceUrl = resolveResourceUrl(resource);
        const response = await api.post<IOrganizationLicensesMetadataResponseItem[]>(resourceUrl, {
          productIds: productIdsToFetch,
        });
        const results = Array.isArray(response.data) ? response.data : [];

        setLicenseData((prev) => {
          const next = new Map(prev);
          results.forEach((item) => {
            fetchedProductIdsRef.current.add(item.productId);
            next.set(item.productId, item.error ? [] : item.licenses ?? []);
          });
          productIdsToFetch.forEach((id) => {
            if (!next.has(id)) {
              fetchedProductIdsRef.current.add(id);
              next.set(id, []);
            }
          });
          return next;
        });
      } catch {
        productIdsToFetch.forEach((id) => fetchedProductIdsRef.current.add(id));
        setLicenseData((prev) => {
          const next = new Map(prev);
          productIdsToFetch.forEach((id) => next.set(id, []));
          return next;
        });
      } finally {
        productIdsToFetch.forEach((id) => fetchingProductIdsRef.current.delete(id));
        setLoadingProductIds((prev) => {
          const next = new Set(prev);
          productIdsToFetch.forEach((id) => next.delete(id));
          return next;
        });
      }
    })();
  }, [productIdsToLoadLicenses, organization, api]);

  const columns = useMemo<GridColumns>(
    () => [
      {
        field: 'amount',
        headerName: t('organization.products.table.amountHeader'),
        width: 80,
        filterOperators: [
          getStringEqualsOperator(),
          getStringDiffersOperator(),
          getGreaterThanOperator(),
          getLessThanOperator(),
        ],
      },
      {
        field: 'productName',
        headerName: t('organization.products.table.productHeader'),
        flex: 2,
      },
      {
        field: 'planName',
        headerName: t('organization.products.table.planHeader'),
        flex: 3,
      },
      {
        field: 'planType',
        headerName: t('common.tableHeader.type'),
        width: 120,
        filterOperators: [
          getEnumIsFilterOperator(['subscription', 'perpetual']),
          getEnumIsNotFilterOperator(['subscription', 'perpetual']),
        ],
      },
      {
        field: 'licensesUsage',
        headerName: t('organization.products.table.licensesAvailableHeader'),
        width: 170,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          // Store grid API reference when available
          if (params.api && !gridApiRef.current) {
            gridApiRef.current = params.api;
          }

          if (!organization) {
            return <NotAssigned>—</NotAssigned>;
          }
          const productId = params.row.id as number;
          const licenses = licenseData.get(productId);
          const isLoading = loadingProductIds.has(productId);
          return (
            <LicenseAvailabilityCell
              key={`license-availability-${productId}`}
              licenses={licenses}
              isLoading={isLoading}
            />
          );
        },
      },
      {
        field: 'expiresAt',
        headerName: t('common.tableHeader.expires'),
        renderCell: (params) => {
          return (
            <LicenseExpiry
              type={params.row.planType}
              expiry={
                params.row.planType === CustomerProductPlanType.Subscription
                  ? params.row.expiresAt
                  : params.row.updatesExpiresAt
              }
            />
          );
        },
        width: 190,
        filterOperators: [getDateBeforeFilterOperator(), getDateAfterFilterOperator()],
      },
      {
        field: 'dealId',
        headerName: 'Deal ID',
        width: 120,
        filterOperators: [getStringEqualsOperator(), getStringDiffersOperator()],
        renderCell: (params) => {
          if (!params.value) {
            return <NotAssigned>—</NotAssigned>;
          }
          return (
            <DealIdLink to={AuthenticatedRoutes.CurrentOrganizationDealHistory(organization?.id, params.row.productId)}>
              {params.value}
            </DealIdLink>
          );
        },
      },
      {
        field: 'status',
        headerName: t('common.tableHeader.status'),
        width: 110,
        filterOperators: [
          getEnumIsFilterOperator({
            [t('common.licenseStatus.valid')]: ['valid', 'expiresSoon'],
            [t('common.licenseStatus.expiresSoon')]: ['expiresSoon'],
            [t('common.licenseStatus.expired')]: ['expired'],
            [t('common.licenseStatus.suspended')]: ['suspended'],
          }),
          getEnumIsNotFilterOperator({
            [t('common.licenseStatus.valid')]: ['valid', 'expiresSoon'],
            [t('common.licenseStatus.expiresSoon')]: ['expiresSoon'],
            [t('common.licenseStatus.expired')]: ['expired'],
            [t('common.licenseStatus.suspended')]: ['suspended'],
          }),
        ],
        renderCell: (params) => {
          if (params.row.corrupted) {
            return <ErrorChip value={'error'} />;
          } else {
            return <StatusChip {...params} />;
          }
        },
      },
      {
        field: 'assignedUsers',
        headerName: t('common.tableHeader.assignedTo'),
        flex: 1,
        filterable: false,
        sortable: false,
        renderCell: (params) => {
          const assignees: UUID[] = params.value;
          const { length } = assignees;
          if (length === 0) {
            return <NotAssigned>{t('products.table.notAssigned')}</NotAssigned>;
          } else if (length === 1) {
            return <LazyUser id={assignees[0]} />;
          } else {
            const tooltipContent = (
              <AssigneesContainer>
                {assignees.map((user) => (
                  <span key={user}>
                    <LazyUser id={user} />
                  </span>
                ))}
              </AssigneesContainer>
            );
            return (
              <Tooltip title={tooltipContent}>
                <span>{t('products.table.assigned', { count: length })}</span>
              </Tooltip>
            );
          }
        },
      },
      {
        field: 'created',
        headerName: t('common.tableHeader.created'),
        width: 120,
        valueFormatter: getDateValueFormatter(DateFormat.DateMedium),
        filterOperators: [getDateBeforeFilterOperator(), getDateAfterFilterOperator()],
      },
      {
        field: 'valid',
        hideable: false,
        sortable: false,
        filterable: true,
        hide: true,
      },
    ],
    [t, organization?.id, licenseData, loadingProductIds]
  );

  const rows = useMemo<GridRowData[]>(() => {
    if (!customerProducts) return [];
    return customerProducts.map((customerProduct) => {
      const status = getLicenseStatus(customerProduct.suspended, customerProduct.expiresAt, customerProduct.planType);
      return {
        ...customerProduct,
        status,
        valid: ['expiresSoon', 'valid'].includes(status),
      };
    });
  }, [customerProducts]);

  const isGetRowLinkAllowed = useIsGuardedRouteAllowed(
    organizationContextType === EOrganizationSelectionStatus.Injected
      ? 'OrganizationProduct'
      : 'CurrentOrganizationProduct',
    [0]
  );
  const getRowLink = useCallback(
    (params: GridRowParams) =>
      organizationContextType === EOrganizationSelectionStatus.Injected
        ? AuthenticatedRoutes.OrganizationProduct(organization?.name, params.row.id)
        : AuthenticatedRoutes.CurrentOrganizationProduct(params.row.id),
    [organizationContextType, organization?.name]
  );

  return (
    <div>
      <GuardNewProduct>
        <ButtonWrapper>
          <Button component={Link} to={newUrl} variant="contained" color="primary">
            {t('organization.products.addButton')}
          </Button>
          {selectedProductIds.length > 1 && (
            <BulkRenewButton variant="contained" color="primary" onClick={handleBulkEdit}>
              Bulk Edit ({selectedProductIds.length})
            </BulkRenewButton>
          )}
          {selectedProductIds.length > 1 && (
            <BulkRenewButton variant="contained" color="secondary" onClick={handleBulkRenew}>
              Bulk Renew ({selectedProductIds.length})
            </BulkRenewButton>
          )}
        </ButtonWrapper>
      </GuardNewProduct>

      <UrlQueryDataGrid
        loading={isLoading}
        columns={columns}
        rows={rows}
        disableExtendRowFullWidth={false}
        autoHeight={true}
        disableSelectionOnClick={false}
        getRowLink={isGetRowLinkAllowed ? getRowLink : void 0}
        checkboxSelection={true}
        selectionModel={selectedRows}
        onSelectionModelChange={handleSelectionChange}
      />

      <CustomerNewProduct open={isNewProductDialogOpen} closeDialog={abortCreate} />

      <CustomerProductBatchRenew
        open={isBatchRenewDialogOpen}
        closeDialog={closeBatchRenewDialog}
        productIds={selectedProductIds.map((id) => parseInt(id, 10))}
      />

      <CustomerProductBulkEdit
        open={isBulkEditDialogOpen}
        closeDialog={closeBulkEditDialog}
        productIds={selectedProductIds.map((id) => parseInt(id, 10))}
      />
    </div>
  );
};
